import { useState, useEffect, useCallback } from "react";
import { Send, MessageSquare, User, Clock, ChevronDown } from "lucide-react";
import { Avatar, Badge, Card } from "../components/UI";
import { PageLoader, ButtonLoader } from "../components/LoadingSpinner";
import { InlineError } from "../components/ErrorAlert";
import usersApi from "../api/users";
import messagesApi from "../api/messages";

const TEMPLATES = [
  "Selamat pagi! Kondisi Anda hari ini terlihat baik berdasarkan data smartwatch. Tetap jaga kesehatan.",
  "Skor EPDS Anda menunjukkan perlu perhatian. Kami sarankan konsultasi dengan dokter minggu ini.",
  "Jangan lupa minum obat dan istirahat yang cukup. Hubungi kami jika ada keluhan.",
  "Detak jantung Anda terpantau sedikit tinggi hari ini. Cobalah untuk beristirahat.",
];

export default function MessagesPage({ preSelectedUser }) {
  const [users, setUsers] = useState([]);
  const [messages, setMessages] = useState([]);
  const [selectedUser, setSelectedUser] = useState(preSelectedUser || null);
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);

  const [usersLoading, setUsersLoading] = useState(true);
  const [msgsLoading, setMsgsLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const [sentOk, setSentOk] = useState(false);

  // ── Fetch users ───────────────────────────────────────────────────────────
  useEffect(() => {
    (async () => {
      try {
        const res = await usersApi.getAll();
        setUsers(res.data?.data ?? res.data ?? []);
      } catch {
        /* non-critical */
      } finally {
        setUsersLoading(false);
      }
    })();
  }, []);

  // ── Fetch messages ────────────────────────────────────────────────────────
  const fetchMessages = useCallback(async () => {
    setMsgsLoading(true);
    try {
      const res = await messagesApi.getAll();
      setMessages(res.data?.data ?? res.data ?? []);
    } catch {
      /* non-critical */
    } finally {
      setMsgsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMessages();
  }, [fetchMessages]);

  // ── Send ──────────────────────────────────────────────────────────────────
  const sendMessage = async () => {
    if (!message.trim() || !selectedUser) return;
    setSending(true);
    setError("");
    setSentOk(false);
    try {
      const res = await messagesApi.send(
        selectedUser.id,
        message.trim(),
        subject.trim(),
      );
      const newMsg = res.data?.data ?? res.data;
      setMessages((prev) => [newMsg, ...prev]);
      setMessage("");
      setSubject("");
      setSentOk(true);
      setTimeout(() => setSentOk(false), 3000);
    } catch (err) {
      setError(err.response?.data?.message || "Gagal mengirim pesan");
    } finally {
      setSending(false);
    }
  };

  const visibleMessages = selectedUser
    ? messages.filter(
        (m) => m.user?.id === selectedUser.id || m.userId === selectedUser.id,
      )
    : messages;

  return (
    <div className="p-6">
      <div className="mb-6">
        <h2 className="font-display font-800 text-2xl text-gray-900">
          Kirim Pesan
        </h2>
        <p className="text-sm text-gray-500 mt-1">
          Kirim pesan informatif kepada ibu nifas (satu arah)
        </p>
      </div>

      <div className="grid lg:grid-cols-2 gap-5">
        {/* Compose */}
        <div className="space-y-4">
          <Card className="p-5">
            <h3 className="font-display font-700 text-gray-900 mb-4">
              Tulis Pesan
            </h3>

            {/* User select */}
            <div className="mb-4 relative">
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Kirim ke
              </label>
              <button
                onClick={() => setShowDropdown(!showDropdown)}
                className="w-full flex items-center justify-between px-4 py-3 border border-gray-200 rounded-xl text-sm bg-white hover:border-primary-300 transition-colors"
              >
                {selectedUser ? (
                  <div className="flex items-center gap-2">
                    <Avatar
                      initials={(selectedUser.name || "U")
                        .slice(0, 2)
                        .toUpperCase()}
                      size="sm"
                    />
                    <span className="font-medium text-gray-900">
                      {selectedUser.name}
                    </span>
                  </div>
                ) : (
                  <span className="text-gray-400">
                    {usersLoading ? "Memuat pengguna..." : "Pilih pengguna..."}
                  </span>
                )}
                <ChevronDown className="w-4 h-4 text-gray-400" />
              </button>
              {showDropdown && (
                <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-xl shadow-lg z-20 overflow-hidden max-h-60 overflow-y-auto">
                  {usersLoading ? (
                    <div className="px-4 py-3 text-sm text-gray-400">
                      Memuat...
                    </div>
                  ) : (
                    users.map((u) => (
                      <button
                        key={u.id}
                        onClick={() => {
                          setSelectedUser(u);
                          setShowDropdown(false);
                        }}
                        className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition-colors text-left"
                      >
                        <Avatar
                          initials={(u.name || "U").slice(0, 2).toUpperCase()}
                          size="sm"
                        />
                        <div>
                          <p className="text-sm font-medium text-gray-900">
                            {u.name}
                          </p>
                          <p className="text-xs text-gray-400">{u.status}</p>
                        </div>
                      </button>
                    ))
                  )}
                </div>
              )}
            </div>

            {/* subject */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Subjek
              </label>
              <input
                type="text"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder="Masukkan subjek pesan..."
                className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>

            {/* Message */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Pesan
              </label>
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Tulis pesan untuk ibu nifas..."
                rows={5}
                className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none"
              />
              <p className="text-xs text-gray-400 mt-1 text-right">
                {message.length} karakter
              </p>
            </div>

            <InlineError message={error} />

            {sentOk && (
              <div className="mb-3 p-3 bg-green-50 border border-green-200 rounded-xl text-sm text-green-700 font-medium">
                ✓ Pesan berhasil dikirim!
              </div>
            )}

            <button
              onClick={sendMessage}
              disabled={
                !message.trim() || !subject.trim() || !selectedUser || sending
              }
              className="w-full flex items-center justify-center gap-2 py-3 bg-primary-800 hover:bg-primary-900 disabled:opacity-40 disabled:cursor-not-allowed text-white font-semibold rounded-xl transition-colors"
            >
              {sending ? (
                <>
                  <ButtonLoader /> Mengirim...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4" /> Kirim Pesan
                </>
              )}
            </button>
          </Card>

          {/* Templates */}
          <Card className="p-5">
            <h3 className="font-display font-700 text-gray-900 mb-3 text-sm">
              Template Pesan Cepat
            </h3>
            <div className="space-y-2">
              {TEMPLATES.map((t, i) => (
                <button
                  key={i}
                  onClick={() => setMessage(t)}
                  className="w-full text-left px-3 py-2.5 rounded-lg border border-gray-100 hover:border-primary-200 hover:bg-primary-50 text-xs text-gray-600 hover:text-primary-800 transition-all leading-relaxed"
                >
                  {t}
                </button>
              ))}
            </div>
          </Card>
        </div>

        {/* History */}
        <Card className="p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-display font-700 text-gray-900">
              Riwayat Pesan
              {selectedUser && (
                <span className="text-primary-700"> — {selectedUser.name}</span>
              )}
            </h3>
            {selectedUser && (
              <button
                onClick={() => setSelectedUser(null)}
                className="text-xs text-gray-400 hover:text-gray-600"
              >
                Tampilkan semua
              </button>
            )}
          </div>

          {msgsLoading ? (
            <PageLoader message="Memuat riwayat pesan..." />
          ) : visibleMessages.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-gray-400">
              <MessageSquare className="w-10 h-10 mb-3 opacity-25" />
              <p className="text-sm">Belum ada pesan dikirim</p>
            </div>
          ) : (
            <div className="space-y-3 max-h-[500px] overflow-y-auto">
              {visibleMessages.map((msg, i) => {
                const msgUser = users.find(
                  (u) => u.id === (msg.user?.id ?? msg.userId),
                );
                return (
                  <div
                    key={msg.id ?? i}
                    className="p-4 rounded-xl bg-gray-50 border border-gray-100"
                  >
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <User className="w-3.5 h-3.5 text-primary-600" />
                        <span className="text-xs font-semibold text-primary-700">
                          → {msgUser?.name || msg.user_name || "Pengguna"}
                        </span>
                        {msgUser?.status && <Badge text={msgUser.status} />}
                      </div>
                      <div className="flex items-center gap-1 text-xs text-gray-400">
                        <Clock className="w-3 h-3" />
                        {msg.sent_at ?? msg.sentAt ?? "—"}
                      </div>
                    </div>
                    {msg.subject && (
                      <p className="text-xs font-semibold text-gray-700 mb-1 truncate">
                        📌 {msg.subject}
                      </p>
                    )}
                    <p className="text-sm text-gray-700 leading-relaxed">
                      {msg.message ?? msg.text}
                    </p>
                    {(msg.sent_by ?? msg.sentBy) && (
                      <p className="text-xs text-gray-400 mt-2">
                        Dikirim oleh: {msg.sent_by ?? msg.sentBy}
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
